import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { 
  BarChart3, Plus, Search, Copy, ExternalLink, 
  Trash2, QrCode, MoreVertical, Home,
  LogOut, Shield, TrendingUp, MousePointerClick, Calendar,
  Menu, X, Lock, Clock, Download, Pencil, Tag, 
  Globe, Layers, Crown, AlertCircle, ChevronDown, ChevronUp,
  Link2, FileText, Eye, Upload, Palette, Type, Settings2, GripVertical
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '../components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu';
import { useAuth } from '../contexts/AuthContext';
import { toast } from 'sonner';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend,
  PieChart, Pie, Cell
} from 'recharts';

const API_URL = window.location.origin + '/api';

const SOURCE_COLORS = {
  facebook: '#1877F2', instagram: '#E4405F', twitter: '#1DA1F2',
  linkedin: '#0A66C2', youtube: '#FF0000', tiktok: '#69C9D0',
  reddit: '#FF4500', pinterest: '#E60023', telegram: '#26A5E4',
  whatsapp: '#25D366', google: '#4285F4', dogrudan: '#06B6D4', diger: '#8B5CF6',
};
const SOURCE_LABELS = {
  facebook: 'Facebook', instagram: 'Instagram', twitter: 'Twitter/X',
  linkedin: 'LinkedIn', youtube: 'YouTube', tiktok: 'TikTok',
  reddit: 'Reddit', pinterest: 'Pinterest', telegram: 'Telegram',
  whatsapp: 'WhatsApp', google: 'Google', dogrudan: 'Doğrudan', diger: 'Diğer',
};
const COUNTRY_COLORS = [
  '#3B82F6', '#10B981', '#F59E0B', '#EF4444', '#8B5CF6',
  '#EC4899', '#06B6D4', '#F97316', '#14B8A6', '#6366F1',
  '#84CC16', '#D946EF', '#0EA5E9', '#FB923C', '#A855F7',
];
const DOMAIN = window.location.host;

const Dashboard = () => {
  const { user, token, logout, isAdmin, isPremium } = useAuth();
  const navigate = useNavigate();
  const [links, setLinks] = useState([]);
  const [stats, setStats] = useState({
    total_links: 0,
    active_links: 0,
    total_clicks: 0,
    today_clicks: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('links');
  
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [newLink, setNewLink] = useState({
    original_url: '',
    custom_slug: '',
    title: '',
    password: '',
    expires_at: '',
    campaign_tag: '',
    utm_source: '',
    utm_medium: '',
    utm_campaign: '',
    utm_term: '',
    utm_content: ''
  });
  const [createLoading, setCreateLoading] = useState(false);
  const [showUtm, setShowUtm] = useState(false);

  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [editingLink, setEditingLink] = useState(null);
  const [editForm, setEditForm] = useState({ short_code: '', title: '', original_url: '' });
  const [editLoading, setEditLoading] = useState(false);

  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedLink, setSelectedLink] = useState(null);

  const [bulkDialogOpen, setBulkDialogOpen] = useState(false);
  const [bulkUrls, setBulkUrls] = useState('');
  const [bulkTag, setBulkTag] = useState('');
  const [bulkLoading, setBulkLoading] = useState(false);

  const [bioPages, setBioPages] = useState([]);
  const [bioDialogOpen, setBioDialogOpen] = useState(false);
  const [newBioPage, setNewBioPage] = useState({ slug: '', title: '', description: '', profile_photo_url: '', logo_url: '' });
  const [bioLoading, setBioLoading] = useState(false);
  const [selectedBioPage, setSelectedBioPage] = useState(null);
  const [bioLinks, setBioLinks] = useState([]);
  const [bioLinkDialogOpen, setBioLinkDialogOpen] = useState(false);
  const [newBioLink, setNewBioLink] = useState({ title: '', url: '', logo_url: '', section_title: '' });
  const [bioAnalytics, setBioAnalytics] = useState(null);
  const [bioAnalyticsLoading, setBioAnalyticsLoading] = useState(false);
  const [editingBioLink, setEditingBioLink] = useState(null);
  const [editBioLinkData, setEditBioLinkData] = useState({ title: '', url: '', logo_url: '', section_title: '' });
  const [bioPageSettingsOpen, setBioPageSettingsOpen] = useState(false);
  const [bioPageSettings, setBioPageSettings] = useState({
    profile_photo_url: '', logo_url: '', title: '', description: '', theme: 'dark',
    bg_type: 'gradient', bg_color: '', bg_gradient_start: '', bg_gradient_end: '', bg_gradient_angle: 135,
    card_style: 'glass', button_style: 'rounded', button_color: '', button_text_color: '',
    font_family: 'Inter', accent_color: '', card_bg_color: '', card_border_color: '',
    text_color: '', subtitle_color: ''
  });
  const [designTab, setDesignTab] = useState('general');
  const [dragIndex, setDragIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const dragItemRef = useRef(null);
  const dragStartY = useRef(0);
  const touchActiveRef = useRef(false);
  const [uploading, setUploading] = useState(false);

  const [slugInfo, setSlugInfo] = useState({ used: 0, limit: 5 });

  const fetchLinks = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setLinks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch links:', error);
      setLinks([]);
    }
  }, [token]);

  const fetchStats = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/analytics/overview`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setStats(response.data || { total_links: 0, active_links: 0, total_clicks: 0, today_clicks: 0 });
    } catch (error) {
      console.error('Failed to fetch stats:', error);
    }
  }, [token]);

  const fetchSlugInfo = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/auth/me`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setSlugInfo({
        used: response.data.custom_slugs_this_month || 0,
        limit: response.data.custom_slugs_limit || 5
      });
    } catch (error) {
      console.error('Failed to fetch slug info:', error);
    }
  }, [token]);

  const fetchBioPages = useCallback(async () => {
    try {
      const response = await axios.get(`${API_URL}/bio/pages`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBioPages(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Failed to fetch bio pages:', error);
    }
  }, [token]);

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await Promise.all([fetchLinks(), fetchStats(), fetchSlugInfo(), fetchBioPages()]);
      setLoading(false);
    };
    loadData();
  }, [fetchLinks, fetchStats, fetchSlugInfo, fetchBioPages]);

  const handleCreateLink = async (e) => {
    e.preventDefault();
    if (!newLink.original_url) {
      toast.error('Lütfen bir URL girin');
      return;
    }
    setCreateLoading(true);
    try {
      const payload = {
        original_url: newLink.original_url,
        custom_slug: newLink.custom_slug || null,
        title: newLink.title || null,
        password: newLink.password || null,
        expires_at: newLink.expires_at ? new Date(newLink.expires_at).toISOString() : null,
        campaign_tag: newLink.campaign_tag || null,
        utm_source: newLink.utm_source || null,
        utm_medium: newLink.utm_medium || null,
        utm_campaign: newLink.utm_campaign || null,
        utm_term: newLink.utm_term || null,
        utm_content: newLink.utm_content || null
      };
      await axios.post(`${API_URL}/links`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Link başarıyla oluşturuldu!');
      setCreateDialogOpen(false);
      setNewLink({ original_url: '', custom_slug: '', title: '', password: '', expires_at: '', campaign_tag: '', utm_source: '', utm_medium: '', utm_campaign: '', utm_term: '', utm_content: '' });
      setShowUtm(false);
      fetchLinks();
      fetchStats();
      fetchSlugInfo();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Link oluşturulamadı');
    }
    setCreateLoading(false);
  };

  const handleDeleteLink = async (linkId) => {
    if (!window.confirm('Bu linki silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/links/${linkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Link silindi');
      fetchLinks();
      fetchStats();
    } catch (error) {
      toast.error('Link silinemedi');
    }
  };

  const handleEditLink = async () => {
    setEditLoading(true);
    try {
      const payload = {};
      if (editForm.short_code !== editingLink.short_code) payload.short_code = editForm.short_code;
      if (editForm.title !== editingLink.title) payload.title = editForm.title;
      if (editForm.original_url !== editingLink.original_url) payload.original_url = editForm.original_url;
      if (Object.keys(payload).length === 0) {
        toast.info('Değişiklik yapılmadı');
        setEditDialogOpen(false);
        return;
      }
      await axios.put(`${API_URL}/links/${editingLink.id}`, payload, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Link güncellendi');
      setEditDialogOpen(false);
      fetchLinks();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncelleme başarısız');
    }
    setEditLoading(false);
  };

  const handleBulkCreate = async () => {
    const urls = bulkUrls.split('\n').map(u => u.trim()).filter(Boolean);
    if (urls.length === 0) { toast.error('En az bir URL girin'); return; }
    if (urls.length > 50) { toast.error('En fazla 50 URL'); return; }
    setBulkLoading(true);
    try {
      const response = await axios.post(`${API_URL}/links/bulk`, {
        urls,
        campaign_tag: bulkTag || null
      }, { headers: { Authorization: `Bearer ${token}` } });
      toast.success(`${response.data.success}/${response.data.total} link oluşturuldu`);
      setBulkDialogOpen(false);
      setBulkUrls('');
      setBulkTag('');
      fetchLinks();
      fetchStats();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Toplu oluşturma başarısız');
    }
    setBulkLoading(false);
  };

  const handleCreateBioPage = async () => {
    if (!newBioPage.slug || !newBioPage.title) { toast.error('Slug ve başlık gerekli'); return; }
    setBioLoading(true);
    try {
      await axios.post(`${API_URL}/bio/pages`, newBioPage, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Bio sayfası oluşturuldu');
      setBioDialogOpen(false);
      setNewBioPage({ slug: '', title: '', description: '', profile_photo_url: '', logo_url: '' });
      fetchBioPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Oluşturulamadı');
    }
    setBioLoading(false);
  };

  const handleDeleteBioPage = async (pageId) => {
    if (!window.confirm('Bu sayfayı silmek istediğinize emin misiniz?')) return;
    try {
      await axios.delete(`${API_URL}/bio/pages/${pageId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sayfa silindi');
      setSelectedBioPage(null);
      fetchBioPages();
    } catch (error) {
      toast.error('Silinemedi');
    }
  };

  const fetchBioLinks = async (pageId) => {
    try {
      const response = await axios.get(`${API_URL}/bio/pages/${pageId}/links`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBioLinks(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      setBioLinks([]);
    }
  };

  const fetchBioAnalytics = async (pageId) => {
    setBioAnalyticsLoading(true);
    try {
      const response = await axios.get(`${API_URL}/bio/pages/${pageId}/analytics`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setBioAnalytics(response.data);
    } catch (error) {
      console.error('Failed to fetch bio analytics:', error);
      setBioAnalytics(null);
    }
    setBioAnalyticsLoading(false);
  };

  const handleAddBioLink = async () => {
    if (!newBioLink.title || !newBioLink.url) { toast.error('Başlık ve URL gerekli'); return; }
    try {
      await axios.post(`${API_URL}/bio/pages/${selectedBioPage.id}/links`, newBioLink, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Link eklendi');
      setBioLinkDialogOpen(false);
      setNewBioLink({ title: '', url: '', logo_url: '', section_title: '' });
      fetchBioLinks(selectedBioPage.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Eklenemedi');
    }
  };

  const handleDeleteBioLink = async (linkId) => {
    try {
      await axios.delete(`${API_URL}/bio/links/${linkId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Link silindi');
      fetchBioLinks(selectedBioPage.id);
    } catch (error) {
      toast.error('Silinemedi');
    }
  };

  const handleEditBioLink = async () => {
    if (!editingBioLink) return;
    if (!editBioLinkData.title || !editBioLinkData.url) { toast.error('Başlık ve URL gerekli'); return; }
    try {
      await axios.put(`${API_URL}/bio/links/${editingBioLink}`, editBioLinkData, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Link güncellendi');
      setEditingBioLink(null);
      setEditBioLinkData({ title: '', url: '', logo_url: '', section_title: '' });
      fetchBioLinks(selectedBioPage.id);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncellenemedi');
    }
  };

  const handleReorderBioLinks = async (newLinks) => {
    setBioLinks(newLinks);
    try {
      await axios.put(`${API_URL}/bio/pages/${selectedBioPage.id}/reorder`, {
        link_ids: newLinks.map(l => l.id)
      }, { headers: { Authorization: `Bearer ${token}` } });
    } catch (error) {
      toast.error('Sıralama kaydedilemedi');
      fetchBioLinks(selectedBioPage.id);
    }
  };

  const handleDragStart = (e, index) => {
    setDragIndex(index);
    dragItemRef.current = index;
    if (e.dataTransfer) {
      e.dataTransfer.effectAllowed = 'move';
      e.dataTransfer.setData('text/plain', index.toString());
    }
  };

  const handleDragOver = (e, index) => {
    e.preventDefault();
    if (dragItemRef.current === null) return;
    setDragOverIndex(index);
  };

  const handleDragEnd = () => {
    if (dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      const newLinks = [...bioLinks];
      const [movedItem] = newLinks.splice(dragIndex, 1);
      newLinks.splice(dragOverIndex, 0, movedItem);
      handleReorderBioLinks(newLinks);
    }
    setDragIndex(null);
    setDragOverIndex(null);
    dragItemRef.current = null;
  };

  const handleTouchStart = (e, index) => {
    const touch = e.touches[0];
    dragStartY.current = touch.clientY;
    dragItemRef.current = index;
    touchActiveRef.current = false;
    setDragIndex(index);
  };

  const handleTouchMove = (e, index) => {
    if (dragItemRef.current === null) return;
    const touch = e.touches[0];
    const diff = Math.abs(touch.clientY - dragStartY.current);
    if (diff > 10) {
      touchActiveRef.current = true;
      e.preventDefault();
    }
    if (!touchActiveRef.current) return;
    const elements = document.querySelectorAll('[data-bio-link-index]');
    for (const el of elements) {
      const rect = el.getBoundingClientRect();
      if (touch.clientY >= rect.top && touch.clientY <= rect.bottom) {
        const targetIdx = parseInt(el.getAttribute('data-bio-link-index'));
        setDragOverIndex(targetIdx);
        break;
      }
    }
  };

  const handleTouchEnd = () => {
    if (touchActiveRef.current && dragIndex !== null && dragOverIndex !== null && dragIndex !== dragOverIndex) {
      const newLinks = [...bioLinks];
      const [movedItem] = newLinks.splice(dragIndex, 1);
      newLinks.splice(dragOverIndex, 0, movedItem);
      handleReorderBioLinks(newLinks);
    }
    setDragIndex(null);
    setDragOverIndex(null);
    dragItemRef.current = null;
    touchActiveRef.current = false;
  };

  const handleTouchCancel = () => {
    setDragIndex(null);
    setDragOverIndex(null);
    dragItemRef.current = null;
    touchActiveRef.current = false;
  };

  const handleUpdateBioPageSettings = async () => {
    try {
      await axios.put(`${API_URL}/bio/pages/${selectedBioPage.id}`, bioPageSettings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Sayfa ayarları güncellendi');
      setBioPageSettingsOpen(false);
      setSelectedBioPage({ ...selectedBioPage, ...bioPageSettings });
      fetchBioPages();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Güncellenemedi');
    }
  };

  const handleImageUpload = async (file, callback) => {
    if (!file) return;
    const maxSize = 5 * 1024 * 1024;
    if (file.size > maxSize) { toast.error('Dosya boyutu 5MB\'dan büyük olamaz'); return; }
    const allowed = ['image/png', 'image/jpeg', 'image/gif', 'image/webp', 'image/svg+xml'];
    if (!allowed.includes(file.type)) { toast.error('Desteklenmeyen dosya formatı'); return; }
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const response = await axios.post(`${API_URL}/upload`, formData, {
        headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'multipart/form-data' }
      });
      callback(response.data.url);
      toast.success('Dosya yüklendi');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Yükleme başarısız');
    }
    setUploading(false);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast.success('Kopyalandı!');
  };

  const getShortUrl = (shortCode) => `https://${DOMAIN}/${shortCode}`;

  const filteredLinks = (Array.isArray(links) ? links : []).filter(link => 
    link?.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    link?.original_url?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    link?.short_code?.toLowerCase().includes(searchQuery.toLowerCase()) ||
    link?.campaign_tag?.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleLogout = () => { logout(); navigate('/'); };

  const canUseCustomSlug = isPremium || isAdmin || slugInfo.used < slugInfo.limit;

  return (
    <div className="min-h-screen bg-[#0A0A0B] flex">
      <aside className={`fixed lg:static inset-y-0 left-0 z-50 w-64 bg-[#121214] border-r border-white/5 transform transition-transform duration-300 lg:transform-none ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}`}>
        <div className="flex flex-col h-full">
          <div className="p-6 border-b border-white/5">
            <Link to="/" className="flex items-center gap-2">
              <img src="/logo.png" alt="BestURL" className="h-9 w-auto" />
            </Link>
          </div>

          <nav className="flex-1 p-4 space-y-2">
            <button onClick={() => setActiveSection('links')} className={`sidebar-nav-item w-full text-left ${activeSection === 'links' ? 'active' : ''}`}>
              <Home className="w-5 h-5" />
              <span>Panel</span>
            </button>
            <button onClick={() => setCreateDialogOpen(true)} className="sidebar-nav-item w-full text-left">
              <Plus className="w-5 h-5" />
              <span>Yeni Link</span>
            </button>
            <button onClick={() => setBulkDialogOpen(true)} className="sidebar-nav-item w-full text-left">
              <Layers className="w-5 h-5" />
              <span>Toplu Link</span>
            </button>
            <button onClick={() => { setActiveSection('bio'); fetchBioPages(); }} className={`sidebar-nav-item w-full text-left ${activeSection === 'bio' ? 'active' : ''}`}>
              <FileText className="w-5 h-5" />
              <span>Link-in-Bio</span>
            </button>
            {isAdmin && (
              <Link to="/admin" className="sidebar-nav-item">
                <Shield className="w-5 h-5" />
                <span>Admin Paneli</span>
              </Link>
            )}
          </nav>

          <div className="p-4 border-t border-white/5">
            {!isPremium && !isAdmin && (
              <div className="mb-3 p-3 rounded-xl bg-amber-500/10 border border-amber-500/20">
                <div className="flex items-center gap-2 mb-1">
                  <Crown className="w-4 h-4 text-amber-400" />
                  <span className="text-xs text-amber-400 font-medium">Özel Slug</span>
                </div>
                <p className="text-xs text-slate-400">{slugInfo.used}/{slugInfo.limit} kullanıldı</p>
                <div className="w-full bg-white/10 rounded-full h-1.5 mt-1">
                  <div className="bg-amber-400 h-1.5 rounded-full" style={{ width: `${Math.min((slugInfo.used / slugInfo.limit) * 100, 100)}%` }} />
                </div>
              </div>
            )}
            <div className="flex items-center gap-3 mb-4">
              <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isPremium ? 'bg-amber-500/20' : 'bg-cyan-500/20'}`}>
                <span className={`font-semibold ${isPremium ? 'text-amber-400' : 'text-cyan-400'}`}>
                  {user?.username?.charAt(0).toUpperCase() || 'U'}
                </span>
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-white font-medium truncate">{user?.username || 'Kullanıcı'}</p>
                <p className="text-xs text-slate-500">
                  {isPremium ? 'Premium' : isAdmin ? 'Admin' : 'Ücretsiz'}
                </p>
              </div>
            </div>
            <Button variant="ghost" className="w-full justify-start text-slate-400 hover:text-white hover:bg-white/5" onClick={handleLogout}>
              <LogOut className="w-4 h-4 mr-2" />
              Çıkış Yap
            </Button>
          </div>
        </div>
      </aside>

      {sidebarOpen && <div className="fixed inset-0 bg-black/50 z-40 lg:hidden" onClick={() => setSidebarOpen(false)} />}

      <main className="flex-1 min-h-screen">
        <header className="sticky top-0 z-30 bg-[#0A0A0B]/80 backdrop-blur-xl border-b border-white/5">
          <div className="flex items-center justify-between px-4 sm:px-6 lg:px-8 h-16">
            <div className="flex items-center gap-4">
              <button className="lg:hidden p-2 text-slate-400 hover:text-white" onClick={() => setSidebarOpen(!sidebarOpen)}>
                {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
              </button>
              <h1 className="text-xl font-semibold text-white">
                {activeSection === 'links' ? 'Panel' : 'Link-in-Bio'}
              </h1>
            </div>
            <div className="flex gap-2">
              <Button onClick={() => setBulkDialogOpen(true)} variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5 hidden sm:flex">
                <Layers className="w-4 h-4 mr-2" />
                Toplu
              </Button>
              <Button onClick={() => setCreateDialogOpen(true)} className="bg-cyan-600 hover:bg-cyan-500 text-white shadow-[0_0_15px_rgba(6,182,212,0.3)]">
                <Plus className="w-4 h-4 mr-2" />
                <span className="hidden sm:inline">Yeni Link</span>
              </Button>
            </div>
          </div>
        </header>

        <div className="p-4 sm:p-6 lg:p-8">
          {activeSection === 'links' && (
            <>
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
                <div className="stat-card">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                      <Link2 className="w-5 h-5 text-cyan-400" />
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Toplam Link</p>
                  <p className="text-2xl font-bold text-white">{stats.total_links || 0}</p>
                </div>
                <div className="stat-card">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-emerald-500/10 flex items-center justify-center">
                      <TrendingUp className="w-5 h-5 text-emerald-400" />
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Aktif Link</p>
                  <p className="text-2xl font-bold text-white">{stats.active_links || 0}</p>
                </div>
                <div className="stat-card">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                      <MousePointerClick className="w-5 h-5 text-purple-400" />
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Toplam Tıklama</p>
                  <p className="text-2xl font-bold text-white">{stats.total_clicks || 0}</p>
                </div>
                <div className="stat-card">
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-10 h-10 rounded-xl bg-amber-500/10 flex items-center justify-center">
                      <Calendar className="w-5 h-5 text-amber-400" />
                    </div>
                  </div>
                  <p className="text-sm text-slate-500 mb-1">Bugünkü Tıklama</p>
                  <p className="text-2xl font-bold text-white">{stats.today_clicks || 0}</p>
                </div>
              </div>

              {stats.daily_clicks_by_source && stats.sources && stats.sources.length > 0 && (
                <div className="bg-[#121214] rounded-2xl border border-white/5 p-4 sm:p-6 mb-8">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg font-semibold text-white">
                      Tıklama Kaynakları {isPremium || isAdmin ? '(Son 30 Gün)' : '(Son 3 Gün)'}
                    </h3>
                    {!isPremium && !isAdmin && (
                      <Link to="/premium" className="text-xs text-amber-400 hover:text-amber-300 flex items-center gap-1">
                        <Crown className="w-3 h-3" />
                        30 gün için Premium'a geç
                      </Link>
                    )}
                  </div>
                  <div className="flex flex-wrap gap-2 mb-4">
                    {Object.entries(stats.source_totals || {})
                      .sort(([,a], [,b]) => b - a)
                      .map(([key, value]) => (
                        <div key={key} className="flex items-center gap-1.5 px-2 py-1 rounded-lg bg-white/5 text-xs">
                          <div className="w-2.5 h-2.5 rounded-sm flex-shrink-0" style={{ backgroundColor: SOURCE_COLORS[key] || '#8B5CF6' }} />
                          <span className="text-slate-300">{SOURCE_LABELS[key] || key}:</span>
                          <span className="text-white font-medium">{value}</span>
                        </div>
                      ))}
                  </div>
                  <ResponsiveContainer width="100%" height={280}>
                    <BarChart data={stats.daily_clicks_by_source}>
                      <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.1)" />
                      <XAxis
                        dataKey="date"
                        stroke="#64748B"
                        tick={{ fontSize: 10 }}
                        tickFormatter={(value) => {
                          try { return new Date(value).toLocaleDateString('tr-TR', { day: '2-digit', month: '2-digit' }); } catch { return value; }
                        }}
                      />
                      <YAxis stroke="#64748B" tick={{ fontSize: 11 }} />
                      <Tooltip
                        content={({ active, payload, label }) => {
                          if (!active || !payload || payload.length === 0) return null;
                          return (
                            <div className="bg-[#1C1C1E] border border-white/10 rounded-lg p-3 shadow-xl">
                              <p className="text-slate-300 text-sm mb-2">
                                {(() => { try { return new Date(label).toLocaleDateString('tr-TR'); } catch { return label; } })()}
                              </p>
                              {payload.filter(p => p.value > 0).map((p, i) => (
                                <div key={i} className="flex items-center gap-2 text-sm">
                                  <div className="w-3 h-3 rounded-sm" style={{ backgroundColor: p.color }} />
                                  <span className="text-slate-400">{SOURCE_LABELS[p.dataKey] || p.dataKey}:</span>
                                  <span className="text-white font-medium">{p.value}</span>
                                </div>
                              ))}
                            </div>
                          );
                        }}
                      />
                      <Legend
                        formatter={(value) => SOURCE_LABELS[value] || value}
                        wrapperStyle={{ fontSize: '11px', paddingTop: '8px' }}
                      />
                      {stats.sources.map((source) => (
                        <Bar key={source} dataKey={source} stackId="a" fill={SOURCE_COLORS[source] || '#8B5CF6'} name={source} />
                      ))}
                    </BarChart>
                  </ResponsiveContainer>
                  {(stats.source_totals || {}).dogrudan > 0 && (
                    <p className="text-xs text-slate-500 mt-3 flex items-center gap-1.5">
                      <AlertCircle className="w-3 h-3 flex-shrink-0" />
                      <span><strong className="text-slate-400">Doğrudan:</strong> Referrer bilgisi olmayan tıklamalar — tarayıcıya doğrudan URL girişi, yer imleri, mesaj uygulamaları (SMS, e-posta), bazı mobil uygulamalar ve gizli pencere tıklamaları bu kategoriye dahildir.</span>
                    </p>
                  )}
                </div>
              )}

              {stats.country_totals && Object.keys(stats.country_totals).length > 0 &&
               !(Object.keys(stats.country_totals).length === 1 && stats.country_totals['Bilinmiyor']) && (
                <div className="bg-[#121214] rounded-2xl border border-white/5 p-4 sm:p-6 mb-8">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-4 sm:mb-6">
                    <h3 className="text-base sm:text-lg font-semibold text-white flex items-center gap-2">
                      <Globe className="w-5 h-5 text-cyan-400" />
                      Ülke Bazlı Tıklamalar
                    </h3>
                  </div>
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                    <div>
                      <ResponsiveContainer width="100%" height={250}>
                        <PieChart>
                          <Pie
                            data={Object.entries(stats.country_totals).slice(0, 10).map(([name, value]) => ({ name, value }))}
                            cx="50%"
                            cy="50%"
                            innerRadius={50}
                            outerRadius={90}
                            paddingAngle={2}
                            dataKey="value"
                          >
                            {Object.entries(stats.country_totals).slice(0, 10).map(([name], index) => (
                              <Cell key={name} fill={COUNTRY_COLORS[index % COUNTRY_COLORS.length]} />
                            ))}
                          </Pie>
                          <Tooltip
                            content={({ active, payload }) => {
                              if (!active || !payload || payload.length === 0) return null;
                              const total = Object.values(stats.country_totals).reduce((a, b) => a + b, 0);
                              return (
                                <div className="bg-[#1C1C1E] border border-white/10 rounded-lg p-3 shadow-xl">
                                  <p className="text-white font-medium text-sm">{payload[0].name}</p>
                                  <p className="text-slate-400 text-xs">{payload[0].value} tıklama ({((payload[0].value / total) * 100).toFixed(1)}%)</p>
                                </div>
                              );
                            }}
                          />
                        </PieChart>
                      </ResponsiveContainer>
                    </div>
                    <div className="space-y-2">
                      {Object.entries(stats.country_totals).slice(0, 10).map(([country, count], index) => {
                        const total = Object.values(stats.country_totals).reduce((a, b) => a + b, 0);
                        const pct = ((count / total) * 100).toFixed(1);
                        return (
                          <div key={country} className="flex items-center gap-3">
                            <div className="w-3 h-3 rounded-full flex-shrink-0" style={{ backgroundColor: COUNTRY_COLORS[index % COUNTRY_COLORS.length] }} />
                            <span className="text-sm text-slate-300 flex-1 truncate">{country}</span>
                            <span className="text-sm text-white font-medium">{count}</span>
                            <div className="w-24 bg-white/5 rounded-full h-2 overflow-hidden">
                              <div className="h-full rounded-full" style={{ width: `${pct}%`, backgroundColor: COUNTRY_COLORS[index % COUNTRY_COLORS.length] }} />
                            </div>
                            <span className="text-xs text-slate-500 w-12 text-right">{pct}%</span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                </div>
              )}

              <div className="bg-[#121214] rounded-2xl border border-white/5 overflow-hidden">
                <div className="p-4 sm:p-6 border-b border-white/5">
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h2 className="text-lg font-semibold text-white">Linklerim</h2>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <Input placeholder="Link ara..." value={searchQuery} onChange={(e) => setSearchQuery(e.target.value)} className="pl-10 w-full sm:w-64 bg-[#1C1C1E] border-white/10 text-white" />
                    </div>
                  </div>
                </div>

                {loading ? (
                  <div className="p-12 text-center"><div className="spinner mx-auto" /></div>
                ) : filteredLinks.length === 0 ? (
                  <div className="p-12 text-center">
                    <div className="empty-state-icon"><Link2 className="w-10 h-10 text-slate-600" /></div>
                    <h3 className="text-lg font-medium text-white mb-2">{searchQuery ? 'Sonuç bulunamadı' : 'Henüz link yok'}</h3>
                    <p className="text-slate-500 mb-6">{searchQuery ? 'Farklı bir arama deneyin' : 'İlk linkinizi oluşturmaya başlayın'}</p>
                    {!searchQuery && (
                      <Button onClick={() => setCreateDialogOpen(true)} className="bg-cyan-600 hover:bg-cyan-500">
                        <Plus className="w-4 h-4 mr-2" />Yeni Link Oluştur
                      </Button>
                    )}
                  </div>
                ) : (
                  <div className="divide-y divide-white/5">
                    {filteredLinks.map((link) => (
                      <div key={link.id} className="p-4 sm:p-6 hover:bg-white/[0.02] transition-colors duration-200">
                        <div className="flex flex-col sm:flex-row sm:items-center gap-4">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="text-white font-medium truncate">{link.title || link.short_code}</h3>
                              {link.has_password && <Lock className="w-4 h-4 text-amber-400 flex-shrink-0" />}
                              {link.expires_at && <Clock className="w-4 h-4 text-slate-400 flex-shrink-0" />}
                              {link.campaign_tag && (
                                <span className="text-xs bg-cyan-500/10 text-cyan-400 px-2 py-0.5 rounded-full">{link.campaign_tag}</span>
                              )}
                              {link.is_custom_slug && (
                                <span className="text-xs bg-purple-500/10 text-purple-400 px-2 py-0.5 rounded-full">Özel</span>
                              )}
                            </div>
                            <div className="flex items-center gap-2 mb-2">
                              <code className="text-cyan-400 text-sm font-mono truncate">{getShortUrl(link.short_code)}</code>
                              <button onClick={() => copyToClipboard(getShortUrl(link.short_code))} className="p-1 text-slate-500 hover:text-cyan-400 flex-shrink-0">
                                <Copy className="w-4 h-4" />
                              </button>
                            </div>
                            <p className="text-sm text-slate-500 truncate">{link.original_url}</p>
                          </div>
                          <div className="flex items-center gap-4">
                            <div className="text-right">
                              <p className="text-lg font-semibold text-white">{link.click_count || 0}</p>
                              <p className="text-xs text-slate-500">tıklama</p>
                            </div>
                            <DropdownMenu>
                              <DropdownMenuTrigger asChild>
                                <Button variant="ghost" size="icon" className="text-slate-400 hover:text-white">
                                  <MoreVertical className="w-5 h-5" />
                                </Button>
                              </DropdownMenuTrigger>
                              <DropdownMenuContent align="end" className="bg-[#1C1C1E] border-white/10">
                                <DropdownMenuItem onClick={() => navigate(`/panel/link/${link.id}`)} className="text-slate-300 hover:text-white focus:text-white focus:bg-white/5">
                                  <BarChart3 className="w-4 h-4 mr-2" />Analitik
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setEditingLink(link); setEditForm({ short_code: link.short_code, title: link.title || '', original_url: link.original_url }); setEditDialogOpen(true); }} className="text-slate-300 hover:text-white focus:text-white focus:bg-white/5">
                                  <Pencil className="w-4 h-4 mr-2" />Düzenle
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => { setSelectedLink(link); setQrDialogOpen(true); }} className="text-slate-300 hover:text-white focus:text-white focus:bg-white/5">
                                  <QrCode className="w-4 h-4 mr-2" />QR Kod
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => window.open(getShortUrl(link.short_code), '_blank')} className="text-slate-300 hover:text-white focus:text-white focus:bg-white/5">
                                  <ExternalLink className="w-4 h-4 mr-2" />Linke Git
                                </DropdownMenuItem>
                                <DropdownMenuItem onClick={() => handleDeleteLink(link.id)} className="text-red-400 hover:text-red-300 focus:text-red-300 focus:bg-red-500/10">
                                  <Trash2 className="w-4 h-4 mr-2" />Sil
                                </DropdownMenuItem>
                              </DropdownMenuContent>
                            </DropdownMenu>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </>
          )}

          {activeSection === 'bio' && (
            <>
              <div className="flex items-center justify-between mb-6">
                <h2 className="text-lg font-semibold text-white">Link-in-Bio Sayfaları</h2>
                <Button onClick={() => setBioDialogOpen(true)} className="bg-cyan-600 hover:bg-cyan-500">
                  <Plus className="w-4 h-4 mr-2" />Yeni Sayfa
                </Button>
              </div>

              {selectedBioPage ? (
                <div>
                  <button onClick={() => { setSelectedBioPage(null); setBioLinks([]); setBioAnalytics(null); }} className="text-cyan-400 hover:text-cyan-300 mb-4 flex items-center gap-1">
                    <ChevronUp className="w-4 h-4" />Geri
                  </button>
                  <div className="bg-[#121214] rounded-2xl border border-white/5 p-6 mb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div>
                        <h3 className="text-xl font-bold text-white">{selectedBioPage.title}</h3>
                        <p className="text-slate-400 text-sm">{selectedBioPage.description || 'Açıklama yok'}</p>
                        <code className="text-cyan-400 text-sm mt-1 block">https://{DOMAIN}/bio/{selectedBioPage.slug}</code>
                      </div>
                      <div className="flex gap-2">
                        <Button onClick={() => copyToClipboard(`https://${DOMAIN}/bio/${selectedBioPage.slug}`)} variant="outline" size="icon" className="border-white/10 text-slate-400">
                          <Copy className="w-4 h-4" />
                        </Button>
                        <Button onClick={() => handleDeleteBioPage(selectedBioPage.id)} variant="outline" size="icon" className="border-red-500/20 text-red-400 hover:bg-red-500/10">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                    <div className="flex gap-2 flex-wrap">
                      <Button onClick={() => setBioLinkDialogOpen(true)} className="bg-cyan-600 hover:bg-cyan-500">
                        <Plus className="w-4 h-4 mr-2" />Link Ekle
                      </Button>
                      <Button onClick={() => { setBioPageSettingsOpen(true); setBioPageSettings({ profile_photo_url: selectedBioPage.profile_photo_url || '', logo_url: selectedBioPage.logo_url || '', title: selectedBioPage.title || '', description: selectedBioPage.description || '', theme: selectedBioPage.theme || 'dark', bg_type: selectedBioPage.bg_type || 'gradient', bg_color: selectedBioPage.bg_color || '', bg_gradient_start: selectedBioPage.bg_gradient_start || '', bg_gradient_end: selectedBioPage.bg_gradient_end || '', bg_gradient_angle: selectedBioPage.bg_gradient_angle || 135, card_style: selectedBioPage.card_style || 'glass', button_style: selectedBioPage.button_style || 'rounded', button_color: selectedBioPage.button_color || '', button_text_color: selectedBioPage.button_text_color || '', font_family: selectedBioPage.font_family || 'Inter', accent_color: selectedBioPage.accent_color || '', card_bg_color: selectedBioPage.card_bg_color || '', card_border_color: selectedBioPage.card_border_color || '', text_color: selectedBioPage.text_color || '', subtitle_color: selectedBioPage.subtitle_color || '' }); }} variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5">
                        <Pencil className="w-4 h-4 mr-2" />Sayfa Ayarları
                      </Button>
                      <Button onClick={() => fetchBioAnalytics(selectedBioPage.id)} variant="outline" className="border-white/10 text-slate-300 hover:bg-white/5">
                        <BarChart3 className="w-4 h-4 mr-2" />Analitik
                      </Button>
                    </div>
                  </div>

                  {bioAnalyticsLoading && (
                    <div className="bg-[#121214] rounded-2xl border border-white/5 p-6 mb-4 text-center">
                      <div className="spinner mx-auto" />
                    </div>
                  )}

                  {bioAnalytics && !bioAnalyticsLoading && (
                    <div className="mb-4 space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div className="bg-[#121214] rounded-2xl border border-white/5 p-4">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-xl bg-purple-500/10 flex items-center justify-center">
                              <Eye className="w-5 h-5 text-purple-400" />
                            </div>
                          </div>
                          <p className="text-sm text-slate-500 mb-1">Görüntülenme</p>
                          <p className="text-2xl font-bold text-white">{bioAnalytics.view_count || 0}</p>
                        </div>
                        <div className="bg-[#121214] rounded-2xl border border-white/5 p-4">
                          <div className="flex items-center gap-3 mb-2">
                            <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                              <MousePointerClick className="w-5 h-5 text-cyan-400" />
                            </div>
                          </div>
                          <p className="text-sm text-slate-500 mb-1">Toplam Tıklama</p>
                          <p className="text-2xl font-bold text-white">{bioAnalytics.total_clicks || 0}</p>
                        </div>
                      </div>

                      {bioAnalytics.link_performance && bioAnalytics.link_performance.length > 0 && (
                        <div className="bg-[#121214] rounded-2xl border border-white/5 overflow-hidden">
                          <div className="p-4 border-b border-white/5">
                            <h4 className="text-white font-medium">Link Performansı</h4>
                          </div>
                          <div className="divide-y divide-white/5">
                            {bioAnalytics.link_performance.map((lp, idx) => (
                              <div key={idx} className="px-4 py-3 flex items-center justify-between">
                                <span className="text-slate-300 text-sm truncate flex-1">{lp.title}</span>
                                <span className="text-cyan-400 text-sm font-medium ml-4">{lp.clicks || 0} tıklama</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {bioAnalytics.devices && Object.keys(bioAnalytics.devices).length > 0 && (
                        <div className="bg-[#121214] rounded-2xl border border-white/5 p-4">
                          <h4 className="text-white font-medium mb-3">Cihaz Dağılımı</h4>
                          <div className="space-y-2">
                            {Object.entries(bioAnalytics.devices).map(([device, count]) => (
                              <div key={device} className="flex items-center justify-between">
                                <span className="text-slate-400 text-sm capitalize">{device}</span>
                                <span className="text-white text-sm font-medium">{count}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}
                    </div>
                  )}

                  {bioLinks.length > 1 && (
                    <p className="text-xs text-slate-500 mb-2 flex items-center gap-1"><GripVertical className="w-3 h-3" /> Sıralamayı değiştirmek için tutup sürükleyin</p>
                  )}
                  <div className="space-y-2">
                    {bioLinks.map((bl, idx) => (
                      <div
                        key={bl.id}
                        data-bio-link-index={idx}
                        draggable={editingBioLink !== bl.id}
                        onDragStart={(e) => handleDragStart(e, idx)}
                        onDragOver={(e) => handleDragOver(e, idx)}
                        onDragEnd={handleDragEnd}
                        onTouchStart={(e) => { if (e.target.closest('[data-drag-handle]')) handleTouchStart(e, idx); }}
                        onTouchMove={(e) => { if (dragItemRef.current !== null) handleTouchMove(e, idx); }}
                        onTouchEnd={handleTouchEnd}
                        onTouchCancel={handleTouchCancel}
                        className={`bg-[#121214] rounded-xl border p-4 transition-all select-none ${
                          dragIndex === idx ? 'opacity-50 border-cyan-500/50 scale-[0.98]' :
                          dragOverIndex === idx ? 'border-cyan-400/60 bg-cyan-500/5 shadow-lg shadow-cyan-500/10' :
                          'border-white/5'
                        }`}
                      >
                        {editingBioLink === bl.id ? (
                          <div className="space-y-3">
                            <Input value={editBioLinkData.title} onChange={(e) => setEditBioLinkData({...editBioLinkData, title: e.target.value})} placeholder="Başlık" className="bg-[#1C1C1E] border-white/10 text-white" />
                            <Input value={editBioLinkData.url} onChange={(e) => setEditBioLinkData({...editBioLinkData, url: e.target.value})} placeholder="URL" className="bg-[#1C1C1E] border-white/10 text-white" />
                            <Input value={editBioLinkData.section_title || ''} onChange={(e) => setEditBioLinkData({...editBioLinkData, section_title: e.target.value})} placeholder="Bölüm başlığı (opsiyonel)" className="bg-[#1C1C1E] border-white/10 text-white" />
                            <div className="flex items-center gap-2">
                              <Input value={editBioLinkData.logo_url || ''} onChange={(e) => setEditBioLinkData({...editBioLinkData, logo_url: e.target.value})} placeholder="Logo URL" className="flex-1 bg-[#1C1C1E] border-white/10 text-white" />
                              <label className="cursor-pointer bg-[#1C1C1E] border border-white/10 rounded-md px-3 py-2 text-xs text-slate-400 hover:text-white hover:border-white/20 transition-colors flex items-center gap-1">
                                <Upload className="w-3 h-3" />Yükle
                                <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e.target.files[0], (url) => setEditBioLinkData({...editBioLinkData, logo_url: url}))} />
                              </label>
                            </div>
                            {editBioLinkData.logo_url && <img src={editBioLinkData.logo_url} alt="" className="w-10 h-10 rounded object-contain" onError={(e) => e.target.style.display='none'} />}
                            <div className="flex gap-2 justify-end">
                              <Button variant="ghost" onClick={() => { setEditingBioLink(null); setEditBioLinkData({ title: '', url: '', logo_url: '', section_title: '' }); }} className="text-slate-400 hover:text-white" size="sm">İptal</Button>
                              <Button onClick={handleEditBioLink} className="bg-cyan-600 hover:bg-cyan-500" size="sm">Kaydet</Button>
                            </div>
                          </div>
                        ) : (
                          <div className="flex items-center justify-between">
                            <div className="flex items-center gap-3 min-w-0 flex-1">
                              <div
                                data-drag-handle
                                className="cursor-grab active:cursor-grabbing p-1.5 -ml-2 rounded-lg hover:bg-white/5 active:bg-white/10 touch-none flex-shrink-0 transition-colors"
                                onMouseDown={(e) => e.stopPropagation()}
                              >
                                <GripVertical className="w-4 h-4 text-slate-500" />
                              </div>
                              {bl.logo_url && <img src={bl.logo_url} alt="" className="w-8 h-8 rounded object-contain flex-shrink-0" onError={(e) => { e.target.style.display = 'none'; }} />}
                              <div className="min-w-0">
                                <p className="text-white font-medium text-sm sm:text-base">{bl.title}</p>
                                <p className="text-slate-400 text-xs sm:text-sm truncate max-w-[120px] sm:max-w-none">{bl.url}</p>
                                {bl.section_title && <span className="text-xs text-cyan-400/60 mt-0.5 inline-block">📁 {bl.section_title}</span>}
                              </div>
                            </div>
                            <div className="flex items-center gap-1 sm:gap-2 ml-2 sm:ml-4 flex-shrink-0">
                              <span className="text-cyan-400 text-xs sm:text-sm whitespace-nowrap hidden sm:inline">{bl.click_count || 0} tıklama</span>
                              <span className="text-cyan-400 text-xs whitespace-nowrap sm:hidden">{bl.click_count || 0}</span>
                              <Button onClick={() => { setEditingBioLink(bl.id); setEditBioLinkData({ title: bl.title, url: bl.url, logo_url: bl.logo_url || '', section_title: bl.section_title || '' }); }} variant="ghost" size="icon" className="text-slate-400 hover:text-white h-8 w-8">
                                <Pencil className="w-3.5 h-3.5" />
                              </Button>
                              <Button onClick={() => handleDeleteBioLink(bl.id)} variant="ghost" size="icon" className="text-red-400 hover:text-red-300 h-8 w-8">
                                <Trash2 className="w-3.5 h-3.5" />
                              </Button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                    {bioLinks.length === 0 && <p className="text-center text-slate-500 py-8">Henüz link eklenmemiş</p>}
                  </div>
                </div>
              ) : (
                <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                  {bioPages.map((page) => (
                    <div key={page.id} className="bg-[#121214] rounded-2xl border border-white/5 p-6 hover:border-cyan-500/30 transition-colors cursor-pointer" onClick={() => { setSelectedBioPage(page); fetchBioLinks(page.id); fetchBioAnalytics(page.id); }}>
                      <div className="flex items-center gap-3 mb-3">
                        <div className="w-10 h-10 rounded-xl bg-cyan-500/10 flex items-center justify-center">
                          <FileText className="w-5 h-5 text-cyan-400" />
                        </div>
                        <div>
                          <h3 className="text-white font-medium">{page.title}</h3>
                          <code className="text-xs text-cyan-400">/{page.slug}</code>
                        </div>
                      </div>
                      <p className="text-sm text-slate-400 line-clamp-2">{page.description || 'Açıklama yok'}</p>
                      <div className="flex items-center gap-1 mt-2 text-slate-500 text-xs">
                        <Eye className="w-3 h-3" />
                        <span>{page.view_count || 0} görüntülenme</span>
                      </div>
                    </div>
                  ))}
                  {bioPages.length === 0 && (
                    <div className="col-span-full text-center py-12">
                      <FileText className="w-12 h-12 text-slate-600 mx-auto mb-4" />
                      <h3 className="text-lg font-medium text-white mb-2">Henüz Bio Sayfası Yok</h3>
                      <p className="text-slate-500 mb-6">Sosyal medya profiliniz için bir link sayfası oluşturun</p>
                      <Button onClick={() => setBioDialogOpen(true)} className="bg-cyan-600 hover:bg-cyan-500">
                        <Plus className="w-4 h-4 mr-2" />İlk Sayfanızı Oluşturun
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </main>

      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Yeni Link Oluştur</DialogTitle>
            <DialogDescription className="text-slate-400">URL'nizi kısaltın ve özelleştirin.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateLink} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">URL *</Label>
              <Input type="url" placeholder="https://ornek.com/uzun-link" value={newLink.original_url} onChange={(e) => setNewLink({...newLink, original_url: e.target.value})} className="bg-[#1C1C1E] border-white/10 text-white" required />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Başlık</Label>
              <Input type="text" placeholder="Kampanya Linki" value={newLink.title} onChange={(e) => setNewLink({...newLink, title: e.target.value})} className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label className="text-slate-300">Özel Kısa URL</Label>
                {!isPremium && !isAdmin && (
                  <span className="text-xs text-slate-500">{slugInfo.used}/{slugInfo.limit} kullanıldı</span>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-sm whitespace-nowrap">{DOMAIN}/</span>
                <Input type="text" placeholder={canUseCustomSlug ? "kampanya" : "Limit doldu"} value={newLink.custom_slug} onChange={(e) => setNewLink({...newLink, custom_slug: e.target.value})} className="flex-1 bg-[#1C1C1E] border-white/10 text-white" disabled={!canUseCustomSlug} />
              </div>
              {!canUseCustomSlug && (
                <p className="text-xs text-amber-400 flex items-center gap-1">
                  <AlertCircle className="w-3 h-3" />Aylık özel slug limitiniz doldu. Boş bırakırsanız rastgele oluşturulur.
                </p>
              )}
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Kampanya Etiketi</Label>
              <Input type="text" placeholder="yaz-kampanyasi" value={newLink.campaign_tag} onChange={(e) => setNewLink({...newLink, campaign_tag: e.target.value})} className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Şifre Koruması</Label>
              <Input type="password" placeholder="••••••••" value={newLink.password} onChange={(e) => setNewLink({...newLink, password: e.target.value})} className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Bitiş Tarihi</Label>
              <Input type="datetime-local" value={newLink.expires_at} onChange={(e) => setNewLink({...newLink, expires_at: e.target.value})} className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>

            <button type="button" onClick={() => setShowUtm(!showUtm)} className="flex items-center gap-2 text-sm text-cyan-400 hover:text-cyan-300">
              <Tag className="w-4 h-4" />UTM Parametreleri
              {showUtm ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
            </button>
            {showUtm && (
              <div className="space-y-3 p-3 rounded-xl bg-white/[0.02] border border-white/5">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">utm_source</Label>
                  <Input placeholder="google, facebook, newsletter" value={newLink.utm_source} onChange={(e) => setNewLink({...newLink, utm_source: e.target.value})} className="h-9 bg-[#1C1C1E] border-white/10 text-white text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">utm_medium</Label>
                  <Input placeholder="cpc, social, email" value={newLink.utm_medium} onChange={(e) => setNewLink({...newLink, utm_medium: e.target.value})} className="h-9 bg-[#1C1C1E] border-white/10 text-white text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">utm_campaign</Label>
                  <Input placeholder="summer_sale" value={newLink.utm_campaign} onChange={(e) => setNewLink({...newLink, utm_campaign: e.target.value})} className="h-9 bg-[#1C1C1E] border-white/10 text-white text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">utm_term</Label>
                  <Input placeholder="anahtar kelime" value={newLink.utm_term} onChange={(e) => setNewLink({...newLink, utm_term: e.target.value})} className="h-9 bg-[#1C1C1E] border-white/10 text-white text-sm" />
                </div>
                <div className="space-y-1">
                  <Label className="text-xs text-slate-400">utm_content</Label>
                  <Input placeholder="banner_ad" value={newLink.utm_content} onChange={(e) => setNewLink({...newLink, utm_content: e.target.value})} className="h-9 bg-[#1C1C1E] border-white/10 text-white text-sm" />
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setCreateDialogOpen(false)} className="text-slate-400 hover:text-white">İptal</Button>
              <Button type="submit" disabled={createLoading} className="bg-cyan-600 hover:bg-cyan-500">
                {createLoading ? <div className="spinner w-4 h-4" /> : 'Oluştur'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>

      <Dialog open={editDialogOpen} onOpenChange={setEditDialogOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Link Düzenle</DialogTitle>
            <DialogDescription className="text-slate-400">Link bilgilerini güncelleyin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Hedef URL</Label>
              <Input type="url" value={editForm.original_url} onChange={(e) => setEditForm({...editForm, original_url: e.target.value})} className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Başlık</Label>
              <Input type="text" value={editForm.title} onChange={(e) => setEditForm({...editForm, title: e.target.value})} className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Kısa Kod (Tag)</Label>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-sm">{DOMAIN}/</span>
                <Input type="text" value={editForm.short_code} onChange={(e) => setEditForm({...editForm, short_code: e.target.value})} className="flex-1 bg-[#1C1C1E] border-white/10 text-white" />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-4">
              <Button type="button" variant="ghost" onClick={() => setEditDialogOpen(false)} className="text-slate-400 hover:text-white">İptal</Button>
              <Button onClick={handleEditLink} disabled={editLoading} className="bg-cyan-600 hover:bg-cyan-500">
                {editLoading ? <div className="spinner w-4 h-4" /> : 'Kaydet'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">QR Kod</DialogTitle>
            <DialogDescription className="text-slate-400">{selectedLink?.title || selectedLink?.short_code}</DialogDescription>
          </DialogHeader>
          <div className="flex flex-col items-center py-6">
            <div id="qr-code-container" className="bg-white rounded-2xl p-6 mb-6">
              {selectedLink && <QRCodeSVG value={getShortUrl(selectedLink.short_code)} size={192} level="H" includeMargin={false} fgColor="#0A0A0B" bgColor="#FFFFFF" />}
            </div>
            <code className="text-cyan-400 text-sm font-mono mb-4">{selectedLink && getShortUrl(selectedLink.short_code)}</code>
            <Button className="bg-cyan-600 hover:bg-cyan-500" onClick={() => {
              const svg = document.querySelector('#qr-code-container svg');
              if (svg) {
                const canvas = document.createElement('canvas');
                canvas.width = 256; canvas.height = 256;
                const ctx = canvas.getContext('2d');
                const data = new XMLSerializer().serializeToString(svg);
                const img = new Image();
                img.onload = () => { ctx.fillStyle = '#FFFFFF'; ctx.fillRect(0, 0, 256, 256); ctx.drawImage(img, 0, 0, 256, 256); const link = document.createElement('a'); link.download = `qr-${selectedLink?.short_code}.png`; link.href = canvas.toDataURL(); link.click(); };
                img.src = 'data:image/svg+xml;base64,' + btoa(unescape(encodeURIComponent(data)));
              }
            }}>
              <Download className="w-4 h-4 mr-2" />PNG İndir
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bulkDialogOpen} onOpenChange={setBulkDialogOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Toplu Link Oluştur</DialogTitle>
            <DialogDescription className="text-slate-400">Her satıra bir URL yazın (maks. 50)</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <textarea value={bulkUrls} onChange={(e) => setBulkUrls(e.target.value)} placeholder={"https://ornek1.com\nhttps://ornek2.com\nhttps://ornek3.com"} className="w-full h-40 bg-[#1C1C1E] border border-white/10 rounded-lg p-3 text-white text-sm resize-none focus:outline-none focus:border-cyan-500/50" />
            <div className="space-y-2">
              <Label className="text-slate-300">Kampanya Etiketi (Opsiyonel)</Label>
              <Input value={bulkTag} onChange={(e) => setBulkTag(e.target.value)} placeholder="kampanya-adi" className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={() => setBulkDialogOpen(false)} className="text-slate-400 hover:text-white">İptal</Button>
              <Button onClick={handleBulkCreate} disabled={bulkLoading} className="bg-cyan-600 hover:bg-cyan-500">
                {bulkLoading ? <div className="spinner w-4 h-4" /> : `${bulkUrls.split('\n').filter(Boolean).length} Link Oluştur`}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bioDialogOpen} onOpenChange={setBioDialogOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Yeni Bio Sayfası</DialogTitle>
            <DialogDescription className="text-slate-400">Sosyal medya profiliniz için link sayfası oluşturun.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Slug *</Label>
              <div className="flex items-center gap-2">
                <span className="text-slate-500 text-sm">{DOMAIN}/bio/</span>
                <Input value={newBioPage.slug} onChange={(e) => setNewBioPage({...newBioPage, slug: e.target.value})} placeholder="isim" className="flex-1 bg-[#1C1C1E] border-white/10 text-white" />
              </div>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Başlık *</Label>
              <Input value={newBioPage.title} onChange={(e) => setNewBioPage({...newBioPage, title: e.target.value})} placeholder="Adınız veya markanız" className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Açıklama</Label>
              <Input value={newBioPage.description} onChange={(e) => setNewBioPage({...newBioPage, description: e.target.value})} placeholder="Kısa bir açıklama" className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Profil Fotoğrafı URL</Label>
              <Input value={newBioPage.profile_photo_url} onChange={(e) => setNewBioPage({...newBioPage, profile_photo_url: e.target.value})} placeholder="https://example.com/photo.jpg" className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Logo URL</Label>
              <Input value={newBioPage.logo_url} onChange={(e) => setNewBioPage({...newBioPage, logo_url: e.target.value})} placeholder="https://example.com/logo.png" className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={() => setBioDialogOpen(false)} className="text-slate-400 hover:text-white">İptal</Button>
              <Button onClick={handleCreateBioPage} disabled={bioLoading} className="bg-cyan-600 hover:bg-cyan-500">
                {bioLoading ? <div className="spinner w-4 h-4" /> : 'Oluştur'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bioLinkDialogOpen} onOpenChange={setBioLinkDialogOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-md">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold">Link Ekle</DialogTitle>
            <DialogDescription className="text-slate-400">Bio sayfanıza yeni link ekleyin.</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-slate-300">Başlık *</Label>
              <Input value={newBioLink.title} onChange={(e) => setNewBioLink({...newBioLink, title: e.target.value})} placeholder="Instagram" className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">URL *</Label>
              <Input value={newBioLink.url} onChange={(e) => setNewBioLink({...newBioLink, url: e.target.value})} placeholder="https://instagram.com/..." className="bg-[#1C1C1E] border-white/10 text-white" />
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Bölüm Başlığı</Label>
              <Input value={newBioLink.section_title} onChange={(e) => setNewBioLink({...newBioLink, section_title: e.target.value})} placeholder="⭐ Öne Çıkanlar" className="bg-[#1C1C1E] border-white/10 text-white" />
              <p className="text-xs text-slate-500">Aynı bölüm başlığına sahip linkler gruplandırılır</p>
            </div>
            <div className="space-y-2">
              <Label className="text-slate-300">Logo</Label>
              <div className="flex items-center gap-2">
                <Input value={newBioLink.logo_url} onChange={(e) => setNewBioLink({...newBioLink, logo_url: e.target.value})} placeholder="Logo URL veya dosya yükleyin" className="flex-1 bg-[#1C1C1E] border-white/10 text-white" />
                <label className="cursor-pointer bg-cyan-600 hover:bg-cyan-500 rounded-md px-3 py-2 text-sm text-white transition-colors flex items-center gap-1 whitespace-nowrap">
                  <Upload className="w-4 h-4" />Yükle
                  <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e.target.files[0], (url) => setNewBioLink({...newBioLink, logo_url: url}))} />
                </label>
              </div>
              {newBioLink.logo_url && <img src={newBioLink.logo_url} alt="" className="w-10 h-10 rounded object-contain mt-1" onError={(e) => e.target.style.display='none'} />}
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="ghost" onClick={() => setBioLinkDialogOpen(false)} className="text-slate-400 hover:text-white">İptal</Button>
              <Button onClick={handleAddBioLink} className="bg-cyan-600 hover:bg-cyan-500">Ekle</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={bioPageSettingsOpen} onOpenChange={setBioPageSettingsOpen}>
        <DialogContent className="bg-[#121214] border-white/10 text-white max-w-lg max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="text-xl font-bold flex items-center gap-2"><Palette className="w-5 h-5 text-cyan-400" />Sayfa Tasarımı</DialogTitle>
            <DialogDescription className="text-slate-400">Bio sayfanızın görünümünü tamamen özelleştirin.</DialogDescription>
          </DialogHeader>
          <div className="mt-4">
            <div className="flex gap-1 mb-6 bg-[#1C1C1E] rounded-lg p-1">
              {[{key:'general',label:'Genel'},{key:'background',label:'Arkaplan'},{key:'cards',label:'Kartlar'},{key:'typography',label:'Yazı'}].map(tab => (
                <button key={tab.key} onClick={() => setDesignTab(tab.key)} className={`flex-1 px-3 py-2 rounded-md text-sm font-medium transition-all ${designTab === tab.key ? 'bg-cyan-600 text-white' : 'text-slate-400 hover:text-white'}`}>{tab.label}</button>
              ))}
            </div>

            {designTab === 'general' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Sayfa Başlığı</Label>
                  <Input value={bioPageSettings.title} onChange={(e) => setBioPageSettings({...bioPageSettings, title: e.target.value})} className="bg-[#1C1C1E] border-white/10 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Açıklama</Label>
                  <Input value={bioPageSettings.description} onChange={(e) => setBioPageSettings({...bioPageSettings, description: e.target.value})} className="bg-[#1C1C1E] border-white/10 text-white" />
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Profil Fotoğrafı</Label>
                  <div className="flex items-center gap-2">
                    <Input value={bioPageSettings.profile_photo_url} onChange={(e) => setBioPageSettings({...bioPageSettings, profile_photo_url: e.target.value})} placeholder="URL veya dosya yükleyin" className="flex-1 bg-[#1C1C1E] border-white/10 text-white" />
                    <label className="cursor-pointer bg-cyan-600 hover:bg-cyan-500 rounded-md px-3 py-2 text-sm text-white transition-colors flex items-center gap-1 whitespace-nowrap">
                      <Upload className="w-4 h-4" />Yükle
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e.target.files[0], (url) => setBioPageSettings({...bioPageSettings, profile_photo_url: url}))} />
                    </label>
                  </div>
                  {bioPageSettings.profile_photo_url && <img src={bioPageSettings.profile_photo_url} alt="" className="w-16 h-16 rounded-full object-cover mt-1" onError={(e) => e.target.style.display='none'} />}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Logo</Label>
                  <div className="flex items-center gap-2">
                    <Input value={bioPageSettings.logo_url} onChange={(e) => setBioPageSettings({...bioPageSettings, logo_url: e.target.value})} placeholder="URL veya dosya yükleyin" className="flex-1 bg-[#1C1C1E] border-white/10 text-white" />
                    <label className="cursor-pointer bg-cyan-600 hover:bg-cyan-500 rounded-md px-3 py-2 text-sm text-white transition-colors flex items-center gap-1 whitespace-nowrap">
                      <Upload className="w-4 h-4" />Yükle
                      <input type="file" accept="image/*" className="hidden" onChange={(e) => handleImageUpload(e.target.files[0], (url) => setBioPageSettings({...bioPageSettings, logo_url: url}))} />
                    </label>
                  </div>
                  {bioPageSettings.logo_url && <img src={bioPageSettings.logo_url} alt="" className="w-20 h-10 object-contain mt-1" onError={(e) => e.target.style.display='none'} />}
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Tema</Label>
                  <div className="flex gap-2">
                    <button onClick={() => setBioPageSettings({...bioPageSettings, theme: 'dark'})} className={`flex-1 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${bioPageSettings.theme === 'dark' ? 'bg-slate-800 border-cyan-500 text-white' : 'bg-[#1C1C1E] border-white/10 text-slate-400 hover:border-white/20'}`}>🌙 Koyu</button>
                    <button onClick={() => setBioPageSettings({...bioPageSettings, theme: 'light'})} className={`flex-1 px-4 py-3 rounded-xl border text-sm font-medium transition-all ${bioPageSettings.theme === 'light' ? 'bg-white border-cyan-500 text-gray-900' : 'bg-[#1C1C1E] border-white/10 text-slate-400 hover:border-white/20'}`}>☀️ Açık</button>
                  </div>
                </div>
              </div>
            )}

            {designTab === 'background' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Arkaplan Tipi</Label>
                  <div className="flex gap-2">
                    <button onClick={() => setBioPageSettings({...bioPageSettings, bg_type: 'gradient'})} className={`flex-1 px-3 py-2 rounded-lg border text-sm ${bioPageSettings.bg_type === 'gradient' ? 'border-cyan-500 text-white bg-cyan-500/10' : 'border-white/10 text-slate-400'}`}>Gradyan</button>
                    <button onClick={() => setBioPageSettings({...bioPageSettings, bg_type: 'solid'})} className={`flex-1 px-3 py-2 rounded-lg border text-sm ${bioPageSettings.bg_type === 'solid' ? 'border-cyan-500 text-white bg-cyan-500/10' : 'border-white/10 text-slate-400'}`}>Düz Renk</button>
                  </div>
                </div>
                {bioPageSettings.bg_type === 'solid' ? (
                  <div className="space-y-2">
                    <Label className="text-slate-300">Arkaplan Rengi</Label>
                    <div className="flex items-center gap-3">
                      <input type="color" value={bioPageSettings.bg_color || '#0a0a0f'} onChange={(e) => setBioPageSettings({...bioPageSettings, bg_color: e.target.value})} className="w-12 h-10 rounded cursor-pointer bg-transparent border-0" />
                      <Input value={bioPageSettings.bg_color || ''} onChange={(e) => setBioPageSettings({...bioPageSettings, bg_color: e.target.value})} placeholder="#0a0a0f" className="flex-1 bg-[#1C1C1E] border-white/10 text-white font-mono text-sm" />
                    </div>
                  </div>
                ) : (
                  <>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Başlangıç Rengi</Label>
                      <div className="flex items-center gap-3">
                        <input type="color" value={bioPageSettings.bg_gradient_start || '#0c0c1d'} onChange={(e) => setBioPageSettings({...bioPageSettings, bg_gradient_start: e.target.value})} className="w-12 h-10 rounded cursor-pointer bg-transparent border-0" />
                        <Input value={bioPageSettings.bg_gradient_start || ''} onChange={(e) => setBioPageSettings({...bioPageSettings, bg_gradient_start: e.target.value})} placeholder="#0c0c1d" className="flex-1 bg-[#1C1C1E] border-white/10 text-white font-mono text-sm" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Bitiş Rengi</Label>
                      <div className="flex items-center gap-3">
                        <input type="color" value={bioPageSettings.bg_gradient_end || '#10142a'} onChange={(e) => setBioPageSettings({...bioPageSettings, bg_gradient_end: e.target.value})} className="w-12 h-10 rounded cursor-pointer bg-transparent border-0" />
                        <Input value={bioPageSettings.bg_gradient_end || ''} onChange={(e) => setBioPageSettings({...bioPageSettings, bg_gradient_end: e.target.value})} placeholder="#10142a" className="flex-1 bg-[#1C1C1E] border-white/10 text-white font-mono text-sm" />
                      </div>
                    </div>
                    <div className="space-y-2">
                      <Label className="text-slate-300">Gradyan Açısı: {bioPageSettings.bg_gradient_angle || 135}°</Label>
                      <input type="range" min="0" max="360" value={bioPageSettings.bg_gradient_angle || 135} onChange={(e) => setBioPageSettings({...bioPageSettings, bg_gradient_angle: parseInt(e.target.value)})} className="w-full accent-cyan-500" />
                    </div>
                  </>
                )}
                <div className="space-y-2">
                  <Label className="text-slate-300">Vurgu Rengi</Label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={bioPageSettings.accent_color || '#06b6d4'} onChange={(e) => setBioPageSettings({...bioPageSettings, accent_color: e.target.value})} className="w-12 h-10 rounded cursor-pointer bg-transparent border-0" />
                    <Input value={bioPageSettings.accent_color || ''} onChange={(e) => setBioPageSettings({...bioPageSettings, accent_color: e.target.value})} placeholder="#06b6d4" className="flex-1 bg-[#1C1C1E] border-white/10 text-white font-mono text-sm" />
                  </div>
                  <p className="text-xs text-slate-500">Avatar çerçevesi, bölüm başlıkları ve hover efektleri</p>
                </div>
                <div className="rounded-xl p-4 border border-white/5" style={{background: bioPageSettings.bg_type === 'solid' ? (bioPageSettings.bg_color || '#0a0a0f') : `linear-gradient(${bioPageSettings.bg_gradient_angle || 135}deg, ${bioPageSettings.bg_gradient_start || '#0c0c1d'}, ${bioPageSettings.bg_gradient_end || '#10142a'})`}}>
                  <p className="text-xs text-center text-slate-400">Arkaplan Önizleme</p>
                </div>
              </div>
            )}

            {designTab === 'cards' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Kart Stili</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[{key:'glass',label:'Cam Efekti',icon:'✨'},{key:'solid',label:'Düz',icon:'⬜'},{key:'outline',label:'Çerçeve',icon:'🔲'},{key:'shadow',label:'Gölge',icon:'🌓'}].map(s => (
                      <button key={s.key} onClick={() => setBioPageSettings({...bioPageSettings, card_style: s.key})} className={`px-3 py-3 rounded-xl border text-sm transition-all flex items-center gap-2 ${bioPageSettings.card_style === s.key ? 'border-cyan-500 text-white bg-cyan-500/10' : 'border-white/10 text-slate-400 hover:border-white/20'}`}>{s.icon} {s.label}</button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Buton Stili</Label>
                  <div className="grid grid-cols-2 gap-2">
                    {[{key:'rounded',label:'Yuvarlak'},{key:'pill',label:'Hap'},{key:'square',label:'Kare'},{key:'outline',label:'Çerçeve'}].map(s => (
                      <button key={s.key} onClick={() => setBioPageSettings({...bioPageSettings, button_style: s.key})} className={`px-3 py-3 rounded-xl border text-sm transition-all ${bioPageSettings.button_style === s.key ? 'border-cyan-500 text-white bg-cyan-500/10' : 'border-white/10 text-slate-400 hover:border-white/20'}`}>{s.label}</button>
                    ))}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Kart Arkaplan Rengi</Label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={bioPageSettings.card_bg_color || '#1a1a2e'} onChange={(e) => setBioPageSettings({...bioPageSettings, card_bg_color: e.target.value})} className="w-12 h-10 rounded cursor-pointer bg-transparent border-0" />
                    <Input value={bioPageSettings.card_bg_color || ''} onChange={(e) => setBioPageSettings({...bioPageSettings, card_bg_color: e.target.value})} placeholder="Otomatik" className="flex-1 bg-[#1C1C1E] border-white/10 text-white font-mono text-sm" />
                    {bioPageSettings.card_bg_color && <button onClick={() => setBioPageSettings({...bioPageSettings, card_bg_color: ''})} className="text-xs text-red-400 hover:text-red-300">Sıfırla</button>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Kart Kenarlık Rengi</Label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={bioPageSettings.card_border_color || '#2a2a3a'} onChange={(e) => setBioPageSettings({...bioPageSettings, card_border_color: e.target.value})} className="w-12 h-10 rounded cursor-pointer bg-transparent border-0" />
                    <Input value={bioPageSettings.card_border_color || ''} onChange={(e) => setBioPageSettings({...bioPageSettings, card_border_color: e.target.value})} placeholder="Otomatik" className="flex-1 bg-[#1C1C1E] border-white/10 text-white font-mono text-sm" />
                    {bioPageSettings.card_border_color && <button onClick={() => setBioPageSettings({...bioPageSettings, card_border_color: ''})} className="text-xs text-red-400 hover:text-red-300">Sıfırla</button>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Buton Rengi</Label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={bioPageSettings.button_color || '#06b6d4'} onChange={(e) => setBioPageSettings({...bioPageSettings, button_color: e.target.value})} className="w-12 h-10 rounded cursor-pointer bg-transparent border-0" />
                    <Input value={bioPageSettings.button_color || ''} onChange={(e) => setBioPageSettings({...bioPageSettings, button_color: e.target.value})} placeholder="Otomatik" className="flex-1 bg-[#1C1C1E] border-white/10 text-white font-mono text-sm" />
                  </div>
                </div>
              </div>
            )}

            {designTab === 'typography' && (
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-slate-300">Yazı Tipi</Label>
                  <select value={bioPageSettings.font_family || 'Inter'} onChange={(e) => setBioPageSettings({...bioPageSettings, font_family: e.target.value})} className="w-full bg-[#1C1C1E] border border-white/10 rounded-lg px-3 py-2 text-white text-sm">
                    {['Inter', 'Poppins', 'Roboto', 'Montserrat', 'Playfair Display', 'Oswald', 'Raleway', 'Nunito', 'Lato', 'Open Sans'].map(f => (
                      <option key={f} value={f}>{f}</option>
                    ))}
                  </select>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Ana Yazı Rengi</Label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={bioPageSettings.text_color || '#f1f5f9'} onChange={(e) => setBioPageSettings({...bioPageSettings, text_color: e.target.value})} className="w-12 h-10 rounded cursor-pointer bg-transparent border-0" />
                    <Input value={bioPageSettings.text_color || ''} onChange={(e) => setBioPageSettings({...bioPageSettings, text_color: e.target.value})} placeholder="Otomatik" className="flex-1 bg-[#1C1C1E] border-white/10 text-white font-mono text-sm" />
                    {bioPageSettings.text_color && <button onClick={() => setBioPageSettings({...bioPageSettings, text_color: ''})} className="text-xs text-red-400 hover:text-red-300">Sıfırla</button>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Alt Yazı Rengi</Label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={bioPageSettings.subtitle_color || '#94a3b8'} onChange={(e) => setBioPageSettings({...bioPageSettings, subtitle_color: e.target.value})} className="w-12 h-10 rounded cursor-pointer bg-transparent border-0" />
                    <Input value={bioPageSettings.subtitle_color || ''} onChange={(e) => setBioPageSettings({...bioPageSettings, subtitle_color: e.target.value})} placeholder="Otomatik" className="flex-1 bg-[#1C1C1E] border-white/10 text-white font-mono text-sm" />
                    {bioPageSettings.subtitle_color && <button onClick={() => setBioPageSettings({...bioPageSettings, subtitle_color: ''})} className="text-xs text-red-400 hover:text-red-300">Sıfırla</button>}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-slate-300">Buton Yazı Rengi</Label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={bioPageSettings.button_text_color || '#f1f5f9'} onChange={(e) => setBioPageSettings({...bioPageSettings, button_text_color: e.target.value})} className="w-12 h-10 rounded cursor-pointer bg-transparent border-0" />
                    <Input value={bioPageSettings.button_text_color || ''} onChange={(e) => setBioPageSettings({...bioPageSettings, button_text_color: e.target.value})} placeholder="Otomatik" className="flex-1 bg-[#1C1C1E] border-white/10 text-white font-mono text-sm" />
                  </div>
                </div>
              </div>
            )}

            <div className="flex justify-end gap-3 pt-6 border-t border-white/5 mt-6">
              <Button variant="ghost" onClick={() => setBioPageSettingsOpen(false)} className="text-slate-400 hover:text-white">İptal</Button>
              <Button onClick={handleUpdateBioPageSettings} className="bg-cyan-600 hover:bg-cyan-500">
                <Palette className="w-4 h-4 mr-2" />Kaydet
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Dashboard;
